import React, { useCallback, useEffect, useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import api from '../services/api';

/**
 * Cadastro de SALAS — o ponto de partida da enturmação.
 *
 * A creche cadastra as salas primeiro; as turmas são montadas a partir delas, e é
 * daqui que sai a capacidade de atendimento da creche.
 *
 * Só nome e uso são exigidos: capacidade e área podem vir depois. Um cadastro que
 * exige tudo de uma vez não é preenchido — e o painel de capacidade aparece já na
 * primeira sala, para o ganho vir antes do esforço terminar.
 */

interface Sala {
  id: number;
  nome: string;
  uso: string;
  uso_rotulo?: string;
  capacidade_criancas: number | null;
  area_m2: number | null;
  observacoes: string | null;
  ativo: boolean;
  capacidade_fisica?: number | null;
  orientacao?: { limite_sugerido: number | null; texto: string; fonte: string };
  turmas?: { id: number; nome: string; turno: string }[];
  periodos_livres?: string[];
  conflitos?: { mensagem: string }[];
}

interface OpcaoUso {
  valor: string;
  rotulo: string;
  descricao: string;
  aceita_turma: boolean;
}

interface Capacidade {
  por_periodo: { matutino: number; vespertino: number };
  simultanea_maxima: number;
  maxima_no_dia: number;
  salas_consideradas: number;
  salas_sem_capacidade_informada: string[];
  salas_fora_do_atendimento: string[];
  parcial: boolean;
}

const PERIODO_LABEL: Record<string, string> = {
  matutino: 'manhã',
  vespertino: 'tarde',
};

export default function SalasPage() {
  const [opcoes, setOpcoes] = useState<OpcaoUso[]>([]);
  const [capacidade, setCapacidade] = useState<Capacidade | null>(null);

  const carregarCapacidade = useCallback(async () => {
    try {
      const { data } = await api.get<Capacidade>('/salas/capacidade-de-atendimento');
      setCapacidade(data);
    } catch {
      setCapacidade(null);
    }
  }, []);

  useEffect(() => {
    api
      .get<OpcaoUso[]>('/salas/opcoes-de-uso')
      .then(({ data }) => setOpcoes(data))
      .catch(() => setOpcoes([]));
    carregarCapacidade();
  }, [carregarCapacidade]);

  return (
    <div>
      {/* ── Capacidade de atendimento: o retorno do cadastro ── */}
      {capacidade && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem 1.25rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>
            Capacidade de atendimento da creche
          </h3>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>
                {capacidade.simultanea_maxima}
              </div>
              <small>crianças ao mesmo tempo (pico)</small>
            </div>
            <div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>
                {capacidade.maxima_no_dia}
              </div>
              <small>crianças diferentes no dia</small>
            </div>
            <div style={{ alignSelf: 'center' }}>
              <div>Manhã: <b>{capacidade.por_periodo.matutino}</b></div>
              <div>Tarde: <b>{capacidade.por_periodo.vespertino}</b></div>
            </div>
          </div>

          {/* O que não pôde ser contado é DITO, nunca somado como zero. */}
          {capacidade.parcial && (
            <div className="alerta alerta-atencao" style={{ marginTop: '0.75rem' }}>
              Números parciais: sem o nº de crianças, estas salas ficaram de fora da
              conta — <b>{capacidade.salas_sem_capacidade_informada.join(', ')}</b>.
            </div>
          )}
          {!!capacidade.salas_fora_do_atendimento?.length && (
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Fora do atendimento (não recebem turma):{' '}
              {capacidade.salas_fora_do_atendimento.join(', ')}.
            </small>
          )}
          {capacidade.salas_consideradas === 0 && !capacidade.parcial && (
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Cadastre a primeira sala para estabelecer a capacidade da creche. Ela
              cresce a cada sala nova.
            </small>
          )}
        </div>
      )}

      <TabelaCrud<Sala>
        titulo="Salas"
        endpoint="/salas"
        onDadosAlterados={carregarCapacidade}
        colunas={[
          { chave: 'nome', label: 'Sala' },
          { chave: 'uso', label: 'Uso', render: (s) => s.uso_rotulo || s.uso },
          {
            chave: 'capacidade_criancas',
            label: 'Crianças/turno',
            render: (s) => (s.capacidade_criancas ?? '—'),
          },
          {
            chave: 'area_m2',
            label: 'Área',
            render: (s) => (s.area_m2 ? `${s.area_m2} m²` : '—'),
          },
          {
            chave: 'turmas',
            label: 'Turmas',
            render: (s) =>
              s.turmas?.length ? s.turmas.map((t) => t.nome).join(', ') : '—',
          },
          {
            chave: 'periodos_livres',
            label: 'Livre',
            render: (s) => {
              if (!s.periodos_livres?.length) return '—';
              return s.periodos_livres.map((p) => PERIODO_LABEL[p] || p).join(' e ');
            },
          },
          {
            chave: 'conflitos',
            label: 'Aviso',
            render: (s) =>
              s.conflitos?.length ? (
                <span title={s.conflitos.map((c) => c.mensagem).join('\n')}>
                  ⚠️ {s.conflitos.length}
                </span>
              ) : (
                '—'
              ),
          },
        ]}
        itemVazio={{
          nome: '',
          uso: 'integral',
          capacidade_criancas: null,
          area_m2: null,
          observacoes: '',
        }}
        validateItem={(item) => {
          if (!item.nome || !String(item.nome).trim()) return 'Informe o nome da sala.';
          return null;
        }}
        renderFormulario={(item, onChange) => {
          const opcao = opcoes.find((o) => o.valor === item.uso);
          return (
            <>
              <label>
                Nome da sala
                <input
                  value={item.nome ?? ''}
                  onChange={(e) => onChange('nome', e.target.value)}
                  placeholder="Ex.: Sala X, Sala Azul, Sala 1"
                />
              </label>

              <label>
                Critério de uso
                <select
                  value={item.uso ?? 'integral'}
                  onChange={(e) => onChange('uso', e.target.value)}
                >
                  {opcoes.map((o) => (
                    <option key={o.valor} value={o.valor}>
                      {o.rotulo}
                    </option>
                  ))}
                </select>
                {opcao && <small>{opcao.descricao}</small>}
              </label>

              <label>
                Crianças por turno
                <input
                  type="number"
                  min={0}
                  value={item.capacidade_criancas ?? ''}
                  onChange={(e) =>
                    onChange(
                      'capacidade_criancas',
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                  placeholder="Ex.: 20"
                />
                <small>
                  Quantas crianças a creche atende nesta sala por período. É daqui que
                  sai a capacidade de atendimento da creche.
                </small>
              </label>

              <label>
                Área da sala (m²) — opcional
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={item.area_m2 ?? ''}
                  onChange={(e) =>
                    onChange('area_m2', e.target.value === '' ? null : Number(e.target.value))
                  }
                  placeholder="Deixe em branco se não souber"
                />
                <small>
                  Informar é opcional. Com a área, o sistema orienta sobre o limite de
                  ocupação por espaço — parâmetro de referência do MEC (1,5 m² por
                  criança), que <b>não é norma nacional vinculante</b>: as regras de
                  área variam por município. Confirme com o Conselho Municipal de
                  Educação.
                </small>
              </label>

              <label className="campo-largo">
                Observações
                <textarea
                  rows={2}
                  value={item.observacoes ?? ''}
                  onChange={(e) => onChange('observacoes', e.target.value)}
                  placeholder="Localização, acessibilidade, restrições de uso…"
                />
              </label>
            </>
          );
        }}
      />
    </div>
  );
}
